import React from 'react';
import {useShallow} from 'zustand/react/shallow';
import {useQuestionStore} from 'client/components/QuestionTypes/V2/useQuestionStores';
import type {QuestionModelV3} from '@albert-io/models';
import LiveRegion from 'lib/LiveRegion';
import Divider from './Divider.react';
import {Item} from './Item.react';
import {OrderTable, OrderTableCell} from './OrderTable.react';
import useQuestionAndDrafts from '../../V2/useQuestionAndDrafts.hook';
import {getItemAccessibleName} from '../../shared/accessibleName';
import {useInterchangeableDuplicateItems} from '../../shared/interchangeableItems';
import {useItemAreaCopy} from '../../shared/itemAreaCopy';
import {useItemMoveCopy} from '../../shared/itemMoveCopy';
import {useAnnouncer} from '../../shared/useAnnouncer';
import {useCancelSelectionOnEscape} from '../../shared/useCancelSelectionOnEscape';
import {useRestoreItemFocus} from '../../shared/useRestoreItemFocus';
import {indexAfterMove} from '../store';
import {
  backWhereItWasMessage,
  deselectedMessage,
  movedMessage,
  selectedMessage
} from './announcements';
import {useInsertPointLabel} from './useInsertPointLabel';
interface Props {
  question: QuestionModelV3;
}
export const ItemList = ({question}: Props) => {
  const useStoreHook = useQuestionStore<'ordering'>(question);

  const {saveDraftGuess} = useQuestionAndDrafts(question);

  const {
    activeItemId,
    items,
    isDragging,
    hoveredDividerId,
    setActiveItemId,
    moveItemToPosition,
    isSubmitted
    // oxlint-disable-next-line react/hooks -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  } = useStoreHook(
    useShallow((state) => ({
      items: state.items,
      activeItemId: state.activeItemId,
      isDragging: state.isDragging,
      hoveredDividerId: state.hoveredDividerId,
      setActiveItemId: state.setActiveItem,
      setHoveredDividerId: state.setHoveredDividerId,
      moveItemToPosition: state.moveItemToPosition,
      isSubmitted: state.isSubmitted
    }))
  );

  const interchangeable = useInterchangeableDuplicateItems();
  const {message, announce} = useAnnouncer();
  const restoreFocusTo = useRestoreItemFocus();
  const copy = useItemMoveCopy();
  const total = items.length;
  const indexOf = (itemId: string | null) => items.findIndex((item) => item.id === itemId);

  const areaCopy = useItemAreaCopy();
  const dividerLabel = useInsertPointLabel(items);

  const handleItemClick = (itemId: string, _e?: React.SyntheticEvent) => {
    if (isDragging) return;

    const index = indexOf(itemId);
    const name =
      index === -1
        ? copy.unnamedItem
        : getItemAccessibleName(items[index].value) || copy.unnamedItem;
    if (activeItemId === itemId) {
      setActiveItemId(null);
      announce(deselectedMessage(copy, name));
    } else {
      setActiveItemId(itemId);
      announce(selectedMessage(copy, name));
    }
  };

  useCancelSelectionOnEscape(activeItemId !== null && !isDragging, () => {
    const index = indexOf(activeItemId);
    setActiveItemId(null);
    if (index === -1 || activeItemId === null) return;
    announce(
      backWhereItWasMessage(copy, getItemAccessibleName(items[index].value) || copy.unnamedItem)
    );
    restoreFocusTo(activeItemId);
  });

  const handleDividerClick = (dividerIndex: number) => {
    if (isDragging || !activeItemId) return;

    const fromIndex = indexOf(activeItemId);
    moveItemToPosition(activeItemId, dividerIndex);
    saveDraftGuess();
    setActiveItemId(null);
    restoreFocusTo(activeItemId);
    if (fromIndex !== -1) {
      const name = getItemAccessibleName(items[fromIndex].value) || copy.unnamedItem;
      announce(movedMessage(copy, name, fromIndex, indexAfterMove(fromIndex, dividerIndex), total));
    }
  };

  const trailingDivider = isSubmitted ? undefined : (
    <Divider
      id={`divider-${total}`}
      label={dividerLabel(total)}
      isVisible={isDragging || activeItemId !== null}
      isHovered={hoveredDividerId === `divider-${total}`}
      onClick={() => handleDividerClick(total)}
    />
  );

  const renderItem = (item: (typeof items)[number], index: number) => (
    <Item
      key={item.id}
      as={isSubmitted ? 'div' : 'li'}
      item={item}
      index={index}
      dividerLabel={dividerLabel(index)}
      trailingDivider={index === total - 1 ? trailingDivider : undefined}
      interchangeable={interchangeable}
      handleItemClick={handleItemClick}
      handleDividerClick={handleDividerClick}
      question={question}
    />
  );

  return (
    <>
      {isSubmitted ? (
        items.length > 0 && (
          <OrderTable label={areaCopy.yourOrder}>
            {items.map((item, index) => (
              <OrderTableCell key={item.id} position={index + 1}>
                {renderItem(item, index)}
              </OrderTableCell>
            ))}
          </OrderTable>
        )
      ) : (
        <div role='group' aria-label={areaCopy.yourOrder}>
          <ol
            role='list'
            className='gap-y-x2 rounded-x1 border-border-primary background-primary p-x2 pl-x0 m-x0 flex list-none flex-wrap items-center border border-solid'
          >
            {items.map(renderItem)}
          </ol>
        </div>
      )}
      <LiveRegion assertive={false} className='sr-only'>
        {message}
      </LiveRegion>
    </>
  );
};

export default ItemList;
